import React, { ComponentType } from 'react';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { connect } from 'react-redux';
import { oc } from 'ts-optchain';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/react-hooks';
import formatMoney from 'services/formatMoney';
import { RootState } from 'store/types';
import { profileEmailsSelector, consumerIdSelector } from 'store/profile/selectors';
import { Email } from 'store/profile/types';
import { billingEncryptionSettingsSelector } from 'store/appSettings/selectors';
import Divider from 'components/UI/Divider';
import { FlexBoxColumn, FlexBoxRow } from 'components/UI/Layout/FlexBox';
import Typography from 'components/UI/Typography';
import { Color } from 'modules/styles/colors';
import { Spacing, FontSize, FontWeight } from 'modules/styles/variables';
import Box from 'components/UI/Layout/Box';
import Spacer from 'components/UI/Layout/Spacer';
import Checkbox from '@material-ui/core/Checkbox';
import SpinnerModal from 'components/UI/Spinner/SpinnerModal';
import WhiteBanner from 'screens/Billing/WhiteBanner';

import {
  StyledTextInput,
  StyledFormField,
  FormContainer,
  StyledTextArea
} from 'screens/Billing/FinancialAssistance/FAApplication/styled';
import analyticsService, { AnalyticsEvent } from 'services/AnalyticsService';
import { PATIENT_FINANCIAL_SERVICE_NUMBER } from 'screens/Billing/FinancialAssistance/FAApplication/constants';
import { DISPUTE_QUESTION_OPTIONS } from './constants';
import { jsEncrypt } from 'services/Encryption.web';
import { NavigationScreenProps } from 'screens/navigation';
import { MuiDialog, MuiBox } from 'theme/material-ui';
import { Select, MenuItem, InputLabel, FormControl, Button } from '@material-ui/core';
import { Alert } from 'components/Alert';

const disputeValidateSchema = Yup.object().shape({
  question: Yup.string()
    .required('Please select a Question Category')
    .nullable(),
  message: Yup.string().required('Please enter a message')
});

interface Props extends NavigationScreenProps {
  consumerId: string;
  profileEmails: Email[];
  displayName: string;
  encryptionSettings: {
    encryption_algorithm: string;
    encryption_iv: string;
    encryption_password: string;
  };
}

interface FAHeader {
  patientName: string;
  applicationId: string;
  guarantorMmi: string;
}

interface BillHeader {
  patientName: string;
  dischargeDate: string;
  encounterID: string;
  billingDescription: string;
  amount: number;
  accountNumber: string;
}
interface FormikValues {
  message: string;
  question: string;
  userReceivesEmail: boolean;
}

export interface MessageComposeProps {
  accountNumber: string;
  amount: number;
  applicationId?: string;
  billingDescription: string;
  dischargeDate: string;
  encounterID: string;
  patientName: string;
  questionCategory?: string;
}

const initialValues = {
  question: '',
  message: '',
  userReceivesEmail: false
};

export const SUBMIT_DISPUTE = gql`
  mutation SendDisputeQuestion($encryptedDispute: String!) {
    postDispute(encryptedDispute: $encryptedDispute)
  }
`;

const RenderBillingHeader = ({
  patientName,
  dischargeDate,
  encounterID,
  billingDescription,
  amount,
  accountNumber
}: BillHeader) => (
  <>
    {dischargeDate ? <Typography>{dayjs(dischargeDate).format('MMMM D, YYYY')}</Typography> : null}
    <Typography>Patient: {patientName}</Typography>
    {accountNumber ? <Typography>Account #: {accountNumber}</Typography> : null}
    {encounterID ? <Typography>Encounter ID: {encounterID}</Typography> : null}
    {billingDescription ? <Typography>Description: {billingDescription}</Typography> : null}
    {amount ? <Typography>Amount: ${formatMoney(amount)}</Typography> : null}
  </>
);

const RenderFAHeader = ({ patientName, applicationId, guarantorMmi }: FAHeader) => (
  <>
    <Typography>Patient: {patientName}</Typography>
    <Typography>Application ID: {applicationId}</Typography>
    <Typography>Guarantor Number: {guarantorMmi}</Typography>
  </>
);

const BillingDisputeScreen = (props: Props) => {
  const { history, location } = props;
  const state = oc(location).state({}) as MessageComposeProps;
  const {
    accountNumber,
    amount,
    applicationId,
    billingDescription,
    dischargeDate,
    encounterID,
    patientName,
    questionCategory
  } = state;

  if (!accountNumber) {
    if (history.length) {
      history.goBack();
    } else {
      history.push('/u/dashboard');
    }
  }
  initialValues.question = questionCategory ? String(questionCategory) : '';
  const [submitMutation, { loading }] = useMutation(SUBMIT_DISPUTE);

  const popupSurvey = () => {
    const timer = setTimeout(() => {
      clearTimeout(timer);
    }, 1000);
  };

  const navigateBack = () => {
    history.goBack();
  };

  const submitDispute = async ({ question, message, userReceivesEmail }: FormikValues) => {
    let disputeDTO = {
      messageBody: `${question}: ${message}`,
      amount,
      admitDate: dischargeDate ? dayjs(dischargeDate).format('MMMM DD, YYYY') : undefined,
      serviceLocation: billingDescription,
      encounterID,
      patientName,
      userReceivesEmail,
      userEmail: oc(props).profileEmails[0].value(''),
      clause: 'submitBillingDispute'
    };

    if (applicationId) {
      disputeDTO = {
        messageBody: `${question}: ${message}`,
        applicationId,
        guarantorNumber: props.consumerId,
        userReceivesEmail: true,
        patientInfo: props.displayName ? props.displayName : 'No Name to Display',
        userEmail: oc(props).profileEmails[0].value(''),
        clause: 'submitFinanceDispute'
      };
    }

    const {
      encryption_algorithm: algorithm,
      encryption_password: pass,
      encryption_iv: IV
    } = props.encryptionSettings;
    const encryptedData = jsEncrypt(algorithm, pass, IV, JSON.stringify(disputeDTO));

    try {
      await submitMutation({
        variables: { encryptedDispute: encryptedData }
      });
      Alert.alert('Your question was successfully sent!', '', [
        {
          text: 'Okay',
          onPress: () => {
            if (!applicationId) popupSurvey();
            props.history.goBack();
          }
        }
      ]);
    } catch (err) {
      Alert.alert('Question did not submit.', 'Sorry for the inconvenience.', [
        {
          text: 'Try Again?',
          onPress: () => submitDispute({ question, message, userReceivesEmail })
        },
        { text: 'Cancel' }
      ]);
    }
  };

  return (
    <MuiDialog fullScreen open onClose={navigateBack}>
      <SpinnerModal isLoading={loading} />
      <FlexBoxColumn>
        <WhiteBanner
          title="Questions About Your Bill"
          showCloseButton
          onCloseButtonPress={navigateBack}
          backgroundColor={Color.white}
          padding={`${FontSize.heading}px`}
        />
        <Divider />
        <Spacer size="small" />

        <Box hSpacing={Spacing.large}>
          <Box hSpacing={Spacing.large}>
            {applicationId ? (
              <RenderFAHeader
                applicationId={applicationId}
                patientName={props.displayName ? props.displayName : 'No Name To Display'}
                guarantorMmi={props.consumerId}
              />
            ) : (
              <RenderBillingHeader
                encounterID={encounterID}
                amount={amount}
                dischargeDate={dischargeDate}
                patientName={patientName}
                billingDescription={billingDescription}
                accountNumber={accountNumber}
              />
            )}
          </Box>

          <Spacer size="small" />
          <Divider />
          <Spacer size="medium" />
          <Box testID="Formik-test-wrapper">
            <Formik
              initialValues={initialValues}
              validationSchema={disputeValidateSchema}
              onSubmit={values => submitDispute(values)}
              validate={values => {
                const errors = {} as { question?: string; message?: string };
                if (!values.question) {
                  errors.question = 'A question category is required';
                }

                if (!values.message) {
                  errors.message = 'A message is required';
                }
                return errors;
              }}
              initialErrors={{ question: 'Please select a category' }}
            >
              {({ values, touched, errors, handleBlur, isValid, setFieldValue, handleSubmit }) => {
                const length = values.message ? values.message.length : 0;
                return (
                  <FormContainer>
                    <FormControl variant="filled">
                      <FlexBoxRow>
                        <InputLabel>Select a Category</InputLabel>
                      </FlexBoxRow>

                      {!questionCategory ? (
                        <Select
                          placeholder="Select a Category"
                          onChange={evt => setFieldValue('question', evt.target.value)}
                          inputProps={{
                            name: 'questionCategory',
                            id: 'questionCategory'
                          }}
                        >
                          {DISPUTE_QUESTION_OPTIONS.map(opt => (
                            <MenuItem key={`dispute_option_${opt.value}`} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <StyledTextInput requiredLabel editable={false} value={questionCategory} />
                      )}
                    </FormControl>
                    <Spacer size="small" />
                    <StyledFormField>
                      <StyledTextArea
                        accessibilityLabel="Message text input"
                        multiline
                        rows={5}
                        requiredLabel
                        maxLength={8192}
                        placeholder="Type your message here..."
                        keyboardType="default"
                        value={values.message}
                        onChange={evt => setFieldValue('message', evt.target.value)}
                        error={touched.message && errors.message ? errors.message : null}
                        onBlur={handleBlur('message')}
                      />
                    </StyledFormField>
                    <Typography>{length}/8192</Typography>
                    <Spacer size="medium" />
                    {oc(props).profileEmails[0].value('') ? (
                      <FlexBoxRow>
                        <Checkbox
                          checked={values.userReceivesEmail}
                          value={values.userReceivesEmail}
                          onChange={(evt, val) => setFieldValue('userReceivesEmail', val)}
                          color="primary"
                        />
                        <Typography lineHeight={FontSize.heading * 2} fontWeight={FontWeight.bold}>
                          I would like an email about this
                        </Typography>
                      </FlexBoxRow>
                    ) : null}
                    <Spacer size="small" />
                    <MuiBox>
                      <MuiBox maxWidth={Spacing.xxxLarge * 3} margin="auto">
                        <Button
                          fullWidth
                          color="primary"
                          variant="contained"
                          disabled={!isValid}
                          onClick={() => {
                            handleSubmit();
                            if (applicationId) {
                              analyticsService.logEvent(AnalyticsEvent.SubmitQuestionClicked);
                            }
                          }}
                        >
                          SUBMIT
                        </Button>
                      </MuiBox>
                    </MuiBox>
                  </FormContainer>
                );
              }}
            </Formik>
          </Box>
          <Spacer size="medium" />
          <Typography textAlign="center">
            If you prefer to speak with a representative call
          </Typography>
          <Spacer size="small" />
          <Typography textAlign="center" color={Color.link}>
            <a href={`tel: ${PATIENT_FINANCIAL_SERVICE_NUMBER}`}>
              {PATIENT_FINANCIAL_SERVICE_NUMBER}
            </a>
          </Typography>
        </Box>
      </FlexBoxColumn>
    </MuiDialog>
  );
};

const mapStateToProps = (state: RootState) => ({
  profileEmails: profileEmailsSelector(state),
  encryptionSettings: billingEncryptionSettingsSelector(state),
  consumerId: consumerIdSelector(state)
});
export { BillingDisputeScreen as BillingDisputeScreenComponent };
export default connect(mapStateToProps)(BillingDisputeScreen as ComponentType);
